import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { css } from '@emotion/core';
import { FilledInput, IconButton, InputAdornment, TextField, Typography } from '@material-ui/core';
import { verifyEmailTypes } from 'features/login/utils/config';
import { auth, firebaseApiErrCodes } from 'features/login/utils/firebaseManager';
import { setVerifyEmailInfo, setInputEmail } from 'store/module/firebase';
import { removeBtnDefaultStyle } from 'styles/common';
import { neutral800 } from '@fever/ui';
import {
    mainButtonSolidStyle,
    errMsgWrapperStyle,
    textFieldDefaultStyle
} from 'features/login/styles/comman';
import { generateEmailValidateRegexp } from 'features/login/utils/validate';
import useHistoryWithCurrentSearchHash from 'features/login/hooks/useHistoryWithCurrentSearchHash';
import { paths, useRedirectApp } from 'features/login/hooks/useInitAndRedirectApp';
import ButtonWithLoadingEffect from 'features/common/ButtonWithLoadingEffect';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

export default function SignInWithEmailPassword({ disabled, onLogin = () => {} }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistoryWithCurrentSearchHash();
    const { currentInputEmail } = useSelector(s => s.firebase);
    const [password, setPassword] = useState('');
    const [emailErrMsg, setEmailErrMsg] = useState('');
    const [passwordErrMsg, setPasswordErrMsg] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [logging, setIsLogging] = useState(false);
    const redirectApp = useRedirectApp();

    function validateInput() {
        let valid = true;
        if (!currentInputEmail) {
            valid = false;
            setEmailErrMsg(i18n.t('label.error.required.email'));
        } else if (!generateEmailValidateRegexp().test(currentInputEmail)) {
            valid = false;
            setEmailErrMsg(i18n.t('label.invalid_email'));
        }
        // 登入只檢查必填，不驗長度／複雜度。存量管理員的舊密碼建立於新政策之前，
        // 在登入端套用政策等同一個壞掉的 force upgrade，會讓他們連登入請求都送不出去；
        // 而且在登入頁回報密碼規則也會向攻擊者洩漏政策細節。
        if (!password) {
            valid = false;
            setPasswordErrMsg(i18n.t('label.error.required.pw'));
        }
        return valid;
    }
    async function handleEmailLogin() {
        try {
            if (validateInput()) {
                onLogin(true);
                setIsLogging(true);
                await signInWithEmailAndPassword(auth, currentInputEmail, password);
                redirectApp({ action: 'doLogin' });
            }
        } catch (error) {
            switch (error.code) {
                // 開啟 Email Enumeration Protection 後，firebase 9.x 會丟 AUTH_INVALID_LOGIN_CREDENTIALS，
                // 新版 SDK 會丟 AUTH_INVALID_CREDENTIAL（詳見 firebaseManager.js）；
                // 保留 AUTH_USER_NOT_FOUND / AUTH_WRONG_PASSWORD 以相容尚未開啟保護的環境。
                // 四者皆導向同一則通用訊息，避免從錯誤訊息反推信箱是否已註冊。
                case firebaseApiErrCodes.AUTH_USER_NOT_FOUND:
                case firebaseApiErrCodes.AUTH_WRONG_PASSWORD:
                case firebaseApiErrCodes.AUTH_INVALID_LOGIN_CREDENTIALS:
                case firebaseApiErrCodes.AUTH_INVALID_CREDENTIAL:
                    setEmailErrMsg(i18n.t('label.error.account.wrong'));
                    break;
                default:
                    console.error('email login', error);
            }
        } finally {
            setIsLogging(false);
            onLogin(false);
        }
    }
    function handleToForgetPassword() {
        dispatch(
            setVerifyEmailInfo({
                verifyEmailType: verifyEmailTypes.FORGET_PASSWORD
            })
        );
        history.push({ pathname: paths.EMAIL_VERIFY });
    }
    function handleEmailChange({ target: { value } }) {
        dispatch(setInputEmail(value));
        setEmailErrMsg('');
    }
    function handlePasswordChange({ target: { value } }) {
        setPassword(value);
        setPasswordErrMsg('');
    }

    return (
        <div>
            <TextField
                variant="filled"
                fullWidth
                value={currentInputEmail}
                onChange={handleEmailChange}
                placeholder={t('placeholder.input_email')}
                css={textFieldDefaultStyle}
                error={Boolean(emailErrMsg)}
            />

            <div css={errMsgWrapperStyle}>
                {Boolean(emailErrMsg) && <Typography variant="caption">{emailErrMsg}</Typography>}
            </div>
            <FilledInput
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={password}
                onChange={handlePasswordChange}
                placeholder={t('placehoder.input_pw')}
                css={textFieldDefaultStyle}
                error={Boolean(passwordErrMsg)}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => {
                                setShowPassword(c => !c);
                            }}
                            edge="end"
                        >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                    </InputAdornment>
                }
            />
            <div css={errMsgWrapperStyle}>
                {Boolean(passwordErrMsg) && (
                    <Typography variant="caption">{passwordErrMsg}</Typography>
                )}
            </div>
            <ButtonWithLoadingEffect
                isLoading={logging}
                disabled={disabled}
                css={mainButtonSolidStyle}
                onClick={handleEmailLogin}
            >
                <Typography variant="subtitle2">{t('btn.login')}</Typography>
            </ButtonWithLoadingEffect>
            <div css={forgetPasswordBtnWrapperStyle}>
                <button
                    css={[forgetPasswordBtnStyle, removeBtnDefaultStyle]}
                    onClick={handleToForgetPassword}
                >
                    {t('btn.to_forget_pw')}
                </button>
            </div>
        </div>
    );
}

const forgetPasswordBtnWrapperStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 0;
`;

const forgetPasswordBtnStyle = css`
    color: ${neutral800};
    font-size: 14px;
`;
